import React, { useEffect, useRef } from "react";
import { autoUpdate } from "@floating-ui/dom";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

interface GearSpotlightProps {
  /** The toolbar's real settings button; the spotlight tracks its rect. */
  anchorRef: React.RefObject<HTMLElement>;
}

/**
 * Figma 410:23163: while the popup is open the gear stays visible above the
 * overlay with a soft white ring. Lifting the real button with z-index can't
 * work — SiteWrapper's `.child_wrapper` (and `.main` on mobile) carry a
 * `transform`, so the button is trapped in a stacking context below
 * ModalLayout's portal. This paints a look-alike instead, rendered inside the
 * popup so it shares the modal's own layer, fixed over the real button's
 * viewport rect.
 */
const GearSpotlight = ({ anchorRef }: GearSpotlightProps) => {
  const cloneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    const clone = cloneRef.current;
    if (!anchor || !clone) {
      return undefined;
    }
    return autoUpdate(anchor, clone, () => {
      const { top, left, width, height } = anchor.getBoundingClientRect();
      Object.assign(clone.style, {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
        visibility: "visible",
      });
    });
  }, [anchorRef]);

  return (
    <div
      ref={cloneRef}
      aria-hidden="true"
      data-testid="calendar-settings-spotlight"
      className={styles.gear_spotlight}
    >
      <i className={cls("ico ico-cog", styles.gear_spotlight_icon)} />
    </div>
  );
};

export default GearSpotlight;
